// useNetworkStatus — track online/offline state with a 'just reconnected' edge.
//
// Used by <OfflineBanner> to show the offline state, by mutation guards to
// disable write actions when offline, and by Apollo client hooks to refetch
// queries on reconnect.

import { useEffect, useRef, useState } from 'react'

export interface NetworkStatus {
  isOnline: boolean
  isOffline: boolean
  /** True for ~2.5s after we transition from offline → online. UI uses this
   *  to show the "Conectat. Sincronizăm…" green confirmation banner. */
  justReconnected: boolean
}

export function useNetworkStatus(): NetworkStatus {
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  )
  const [justReconnected, setJustReconnected] = useState(false)
  const prevOnlineRef = useRef(isOnline)

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  useEffect(() => {
    if (!prevOnlineRef.current && isOnline) {
      setJustReconnected(true)
      const timeout = window.setTimeout(() => setJustReconnected(false), 2500)
      prevOnlineRef.current = isOnline
      return () => window.clearTimeout(timeout)
    }
    prevOnlineRef.current = isOnline
  }, [isOnline])

  return { isOnline, isOffline: !isOnline, justReconnected }
}
